/**
 * Privacy settings component.
 * Allows users to control privacy-related settings like hiding account from searches.
 */
import React, { useState } from 'react';
import type { UpdateProfileData, UserProfile } from '../types';

interface PrivacySettingsProps {
  profile: UserProfile;
  onUpdate: (data: UpdateProfileData) => Promise<void>;
}

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({ profile, onUpdate }) => {
  const [hideAccount, setHideAccount] = useState(profile.hide_account || false);
  const [hideLinkedBadges, setHideLinkedBadges] = useState(
    profile.show_linked_account_badges_to_friends === false
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    setHideAccount(profile.hide_account || false);
    setHideLinkedBadges(profile.show_linked_account_badges_to_friends === false);
  }, [profile.hide_account, profile.show_linked_account_badges_to_friends]);

  const handleUpdate = async (
    nextState: { hideAccount?: boolean; hideLinkedBadges?: boolean },
    updateData: UpdateProfileData
  ) => {
    const previousHideAccount = hideAccount;
    const previousHideLinkedBadges = hideLinkedBadges;

    if (nextState.hideAccount !== undefined) {
      setHideAccount(nextState.hideAccount);
    }
    if (nextState.hideLinkedBadges !== undefined) {
      setHideLinkedBadges(nextState.hideLinkedBadges);
    }

    setIsUpdating(true);
    setError(null);
    setSuccess(false);

    try {
      await onUpdate(updateData);
      setSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      // Revert on error
      setHideAccount(previousHideAccount);
      setHideLinkedBadges(previousHideLinkedBadges);
      setError(err instanceof Error ? err.message : 'Failed to update privacy setting');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleHideAccountToggle = () => {
    const newValue = !hideAccount;
    handleUpdate({ hideAccount: newValue }, { hide_account: newValue });
  };

  const handleHideLinkedBadgesToggle = () => {
    const newValue = !hideLinkedBadges;
    handleUpdate(
      { hideLinkedBadges: newValue },
      { show_linked_account_badges_to_friends: !newValue }
    );
  };

  const toggleClassName = (enabled: boolean) => `
    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
    ${enabled ? 'bg-green-600' : 'bg-gray-200'}
    ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
  `;

  const knobClassName = (enabled: boolean) => `
    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
    transition duration-200 ease-in-out
    ${enabled ? 'translate-x-5' : 'translate-x-0'}
  `;

  return (
    <div className="space-y-4">
      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">Privacy setting updated successfully!</div>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900">Hide account from friend searches</h3>
            <div className="relative group">
              <img
                src="/icon-info-lrg.png"
                alt="Information"
                className="h-4 w-4 cursor-help"
                aria-label="Information"
              />
              {/* Tooltip */}
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-10">
                <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg w-64">
                  <p className="text-left">
                    When enabled, your account will not appear in friend search results. 
                    Existing friends will still be able to see you in their friends list.
                  </p>
                  {/* Tooltip arrow */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4">
          <button
            type="button"
            onClick={handleHideAccountToggle}
            disabled={isUpdating}
            className={toggleClassName(hideAccount)}
            role="switch"
            aria-checked={hideAccount}
            aria-label="Hide account from friend searches"
          >
            <span className={knobClassName(hideAccount)} />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-900">Hide linked account badges from friends</h3>
            <div className="relative group">
              <img
                src="/icon-info-lrg.png"
                alt="Information"
                className="h-4 w-4 cursor-help"
                aria-label="Information"
              />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-10">
                <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg w-64">
                  <p className="text-left">
                    When enabled, friends who can see your account rows will not see which accounts are linked externally.
                  </p>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4">
          <button
            type="button"
            onClick={handleHideLinkedBadgesToggle}
            disabled={isUpdating}
            className={toggleClassName(hideLinkedBadges)}
            role="switch"
            aria-checked={hideLinkedBadges}
            aria-label="Hide linked account badges from friends"
          >
            <span className={knobClassName(hideLinkedBadges)} />
          </button>
        </div>
      </div>
    </div>
  );
};
